/**
 * Supabase Client Configuration
 * Optimized for Claude Code + Supabase development
 * Beta Swarm Ready Configuration
 *
 * Uses centralized config from src/config/supabase-config.js
 * To switch between custom domain and direct URL, update supabase-config.js
 *
 * ARCHITECTURE: Single unified client pattern
 * - ONE Supabase client handles both data queries AND authentication
 * - Eliminates "Multiple GoTrueClient instances" warnings
 * - The `supabase` export is the primary client for all operations
 * - The `auth` export provides direct access to auth methods
 *
 * HMR/STRICTMODE SAFETY:
 * - Uses window (browser) to persist client across HMR reloads
 * - Checks for existing client BEFORE creating a new one
 * - This prevents navigator.locks deadlocks in development
 */

import { createClient } from '@supabase/supabase-js'
import { createScopedLogger } from '../utils/logger.js'
import { SUPABASE_CONFIG, getAuthConfig } from '../config/supabase-config.js'

const log = createScopedLogger('supabase')
const isBrowser = typeof window !== 'undefined';

// Use window in browser (persists across HMR), globalThis elsewhere
const globalScope = isBrowser ? window : (typeof globalThis !== 'undefined' ? globalThis : {});

// Single global key for the unified client
const GLOBAL_KEY = '__ODV_SUPABASE_CLIENT__'
const ADMIN_KEY = '__ODV_SUPABASE_ADMIN_CLIENT__'
const FUNCTIONS_KEY = '__ODV_SUPABASE_FUNCTIONS_CLIENT__'

// Import configuration from centralized config
const supabaseUrl = SUPABASE_CONFIG.url
const supabaseFunctionsUrl = SUPABASE_CONFIG.functionsUrl
const supabaseAnonKey = SUPABASE_CONFIG.anonKey
const supabaseServiceKey = SUPABASE_CONFIG.serviceRoleKey

// Track warning state globally to prevent duplicate warnings across HMR
let browserAdminWarningShown = globalScope.__ODV_ADMIN_WARNING_SHOWN__ ?? false;

/**
 * UNIFIED Supabase client - handles BOTH data queries AND authentication
 * This eliminates the "Multiple GoTrueClient instances" warning
 *
 * HMR Safety: Check global scope FIRST before creating a new client.
 * The previous pattern used module-level variables which get reset on HMR,
 * but window.* persists across hot reloads.
 */
export const supabase = (() => {
  // CRITICAL: Check window/globalScope first - this persists across HMR
  if (globalScope[GLOBAL_KEY]) {
    log.debug?.('Reusing existing Supabase client (HMR-safe)');
    return globalScope[GLOBAL_KEY];
  }

  // Create new client only if none exists
  // detectSessionInUrl is always false - we handle OAuth code exchange manually in AuthContext
  const authConfig = getAuthConfig();

  const client = createClient(supabaseUrl, supabaseAnonKey, {
    auth: authConfig,
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    },
    global: {
      headers: isBrowser ? undefined : {
        'x-application-name': 'odv-platform',
      }
    }
  });

  // Store in global scope (survives HMR)
  globalScope[GLOBAL_KEY] = client;

  log.debug?.('Supabase UNIFIED client initialised', {
    url: supabaseUrl,
    redirectUrl: SUPABASE_CONFIG.redirectUrl,
  });

  return client;
})();

// For backwards compatibility - getAuthClient returns the same unified client
export const getAuthClient = () => supabase;

// Service role client for admin operations (SERVER-SIDE ONLY!)
// SECURITY: Never expose service keys in browser environment

export const getSupabaseAdmin = () => {
  if (isBrowser) {
    if (!browserAdminWarningShown) {
      log.warn?.('Admin client requested in browser - returning RLS-protected client. Use Edge Functions for elevated access.');
      browserAdminWarningShown = true;
    }
    return supabase; // Return regular client with RLS protection instead
  }

  // Server-side only: Create admin client with service role key
  if (!globalScope[ADMIN_KEY]) {
    if (!supabaseServiceKey) {
      throw new Error('SUPABASE_SERVICE_ROLE_KEY not configured. This is only needed for server-side operations.');
    }
    const supabaseAdminInstance = createClient(supabaseUrl, supabaseServiceKey, {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
        storage: undefined
      },
      global: {
        headers: {
          'x-application-name': 'odv-truest-admin',
          'x-client-info': 'admin-service-role'
        }
      }
    })
    globalScope[ADMIN_KEY] = supabaseAdminInstance
  }
  return globalScope[ADMIN_KEY];
};

// Supabase Functions client for edge function calls
export const getSupabaseFunctions = () => {
  if (!globalScope[FUNCTIONS_KEY]) {
    const supabaseFunctionsInstance = createClient(supabaseFunctionsUrl, supabaseServiceKey, {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
        storage: undefined
      },
      global: {
        headers: {
          'x-application-name': 'odv-truest-functions',
          'x-client-info': 'functions-service-role'
        }
      }
    });
    globalScope[FUNCTIONS_KEY] = supabaseFunctionsInstance
  }
  return globalScope[FUNCTIONS_KEY];
};

// Legacy Firebase-style exports for easier migration
export const db = supabase  // Data client for queries

// Direct auth export - no proxy needed with unified client
export const auth = supabase.auth;

export const storage = supabase.storage

// syncSession is now a no-op since we use a unified client
// Keeping for backwards compatibility with AuthContext
const syncSession = async (_sessionFromCallback = null) => {
  // No-op: With unified client, session is automatically available
  // This function exists for backwards compatibility
  log.debug?.('syncSession called (no-op with unified client)');
};

export { syncSession };

/**
 * Session-guarded Supabase operation wrapper
 *
 * Solves: token refresh hanging in mobile/incognito browsers causing infinite spinners.
 *
 * Before running your DB operation it:
 * 1. Checks if the current session is expiring soon (<5 min) and proactively refreshes
 * 2. Wraps your operation in a timeout (default 8s)
 * 3. If the operation fails with a session/JWT error, refreshes and retries once
 *
 * Usage:
 *   const { data, error } = await withSessionGuard(() =>
 *     supabase.from('valets').update(payload).eq('id', id)
 *   );
 */
export const withSessionGuard = async (operation, options = {}) => {
  const { timeout = 8000, retries = 1 } = options;

  const refreshWithTimeout = async () => {
    const result = await Promise.race([
      auth.refreshSession(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Session refresh timed out')), 5000)
      )
    ]);
    if (result.error) throw result.error;
    return result.data.session;
  };

  // Step 1: Proactive refresh if session is expiring soon
  try {
    const { data: { session } } = await auth.getSession();
    if (session?.expires_at) {
      const expiresAt = session.expires_at * 1000;
      const fiveMinutes = 5 * 60 * 1000;
      if (expiresAt - Date.now() < fiveMinutes) {
        log.debug?.('Session expiring soon, proactively refreshing');
        await refreshWithTimeout();
      }
    }
  } catch (refreshErr) {
    log.warn?.('Proactive session refresh failed, proceeding anyway', refreshErr.message);
  }

  // Step 2: Execute with timeout + retry
  let lastError = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const result = await Promise.race([
        operation(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timed out. Please check your connection and try again.')), timeout)
        )
      ]);
      return result;
    } catch (err) {
      lastError = err;
      const msg = err?.message?.toLowerCase() || '';
      const isSessionError = msg.includes('jwt') || msg.includes('session') || msg.includes('refresh_token') || msg.includes('timed out');

      if (isSessionError && attempt < retries) {
        log.debug?.(`Session error on attempt ${attempt + 1}, refreshing and retrying`);
        try {
          await refreshWithTimeout();
        } catch (refreshErr) {
          log.error?.('Session refresh failed during retry', refreshErr.message);
          throw new Error('Your session expired. Please refresh the page and try again.');
        }
        continue;
      }
      throw err;
    }
  }
  throw lastError;
};

/**
 * Get an authenticated query client
 *
 * Returns the unified Supabase client which already has auth session.
 * This prevents creating duplicate GoTrueClient instances.
 *
 * Previously we created separate "query" and "fresh" clients, but this caused
 * "Multiple GoTrueClient instances detected" warnings and wasn't necessary
 * since the unified client already handles all queries with the current auth session.
 */
export const getAuthedQueryClient = async () => {
  log.debug?.('getAuthedQueryClient: returning unified client (no duplication)');
  return supabase;
};

// Helper functions for common operations
export const supabaseHelpers = {
  // Activity Logs
  async logActivity(action, entityType, entityId, userId, metadata) {
    return await supabase
      .from('activity_logs')
      .insert({
        action,
        entity_type: entityType,
        entity_id: entityId,
        user_id: userId,
        metadata
      })
  },

  // Clients
  async getActiveClients() {
    return await supabase
      .from('clients')
      .select('*')
      .eq('status', 'active')
      .order('name')
  },

  async getClientById(id) {
    return await supabase
      .from('clients')
      .select('*')
      .eq('id', id)
      .single()
  },

  // Products
  async getActiveProducts() {
    return await supabase
      .from('products')
      .select('*')
      .eq('status', 'active')
      .order('name')
  },

  // Assignments (Beta Swarm Ready)
  async getAssignmentsByStatus(status) {
    return await supabase
      .from('assignments')
      .select(`
        *,
        clients(name, email),
        user_stats(power_score)
      `)
      .eq('status', status)
      .order('created_at', { ascending: false })
  },

  async updateAssignmentStatus(id, status, metadata) {
    return await supabase
      .from('assignments')
      .update({ 
        status,
        metadata,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
  },

  // Badges (Beta Swarm Ready)
  async getUserBadges(userId) {
    return await supabase
      .from('badges')
      .select('*')
      .eq('user_id', userId)
      .order('earned_at', { ascending: false })
  },

  async awardBadge(userId, badgeType, badgeName, xpValue, metadata) {
    return await supabase
      .from('badges')
      .insert({
        user_id: userId,
        badge_type: badgeType,
        badge_name: badgeName,
        xp_value: xpValue,
        metadata
      })
  },

  // user_stats helpers removed 2026-10-11: public.user_stats has never existed, and
  // nothing called getUserStats / updateUserStats / subscribeToUserStats (db-touch audit).

  // Real-time subscriptions
  subscribeToAssignments(callback) {
    return supabase
      .channel('assignments')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'assignments' }, 
        callback
      )
      .subscribe()
  }
}

// Export types for convenience (TypeScript only)
// export type { Database } from './types/supabase'
// export type {
//   ActivityLog,
//   Client,
//   Product,
//   Assignment,
//   Badge,
//   UserStats,
//   ActivityLogInsert,
//   ClientInsert,
//   ProductInsert,
//   AssignmentInsert,
//   BadgeInsert,
//   UserStatsInsert
// } from './types/supabase'

// Configuration info for debugging - re-export centralized config
export const supabaseConfig = {
  ...SUPABASE_CONFIG,
  hasServiceKey: !!supabaseServiceKey
}

// Development helpers
if (import.meta.env.DEV) {
  log.debug?.('Supabase client initialized', {
    url: supabaseConfig.url,
    redirectUrl: supabaseConfig.redirectUrl,
    project: supabaseConfig.project,
    useCustomDomain: supabaseConfig.useCustomDomain,
    hasServiceKey: supabaseConfig.hasServiceKey
  })
}

// Firebase Compatibility Layer - Firestore-like APIs
// These functions provide Firebase-like APIs using Supabase underneath
// to ease the migration process

// Firestore-like collection API
export const collection = (dbRef, collectionName) => {
  return {
    _collectionName: collectionName,
    _supabase: dbRef
  };
};

// Firestore-like getDocs API
export const getDocs = async (collectionRef) => {
  const { data, error } = await collectionRef._supabase
    .from(collectionRef._collectionName)
    .select('*');
  
  if (error) throw error;
  
  return {
    docs: data.map(item => ({
      id: item.id,
      data: () => item,
      exists: () => true
    })),
    empty: data.length === 0,
    size: data.length };
};

// Firestore-like doc API
export const doc = (dbRef, collectionName, docId) => {
  return {
    _collectionName: collectionName,
    _docId: docId,
    _supabase: dbRef
  };
};

// Firestore-like getDoc API
export const getDoc = async (docRef) => {
  const { data, error } = await docRef._supabase
    .from(docRef._collectionName)
    .select('*')
    .eq('id', docRef._docId)
    .single();
  
  if (error && error.code !== 'PGRST116') throw error;
  
  return {
    id: data?.id,
    data: () => data,
    exists: () => !!data
  };
};

// Firestore-like setDoc API
export const setDoc = async (docRef, data, options = {}) => {
  const { merge = false } = options;
  
  if (merge) {
    const { error } = await docRef._supabase
      .from(docRef._collectionName)
      .update(data)
      .eq('id', docRef._docId);
    
    if (error) throw error;
  } else {
    const { error } = await docRef._supabase
      .from(docRef._collectionName)
      .upsert({ ...data, id: docRef._docId });
    
    if (error) throw error;
  }
};

// Firestore-like addDoc API
export const addDoc = async (collectionRef, data) => {
  const { data: inserted, error } = await collectionRef._supabase
    .from(collectionRef._collectionName)
    .insert(data)
    .select()
    .single();
  
  if (error) throw error;
  
  return {
    id: inserted.id };
};

// Firestore-like updateDoc API
export const updateDoc = async (docRef, data) => {
  const { error } = await docRef._supabase
    .from(docRef._collectionName)
    .update(data)
    .eq('id', docRef._docId);
  
  if (error) throw error;
};

// Firestore-like deleteDoc API
export const deleteDoc = async (docRef) => {
  const { error } = await docRef._supabase
    .from(docRef._collectionName)
    .delete()
    .eq('id', docRef._docId);
  
  if (error) throw error;
};

// Firestore-like query API
export const query = (collectionRef, ...constraints) => {
  let queryBuilder = collectionRef._supabase
    .from(collectionRef._collectionName)
    .select('*');
  
  for (const constraint of constraints) {
    queryBuilder = constraint(queryBuilder);
  }
  
  return {
    _query: queryBuilder,
    _supabase: collectionRef._supabase };
};

// Firestore-like where constraint
export const where = (field, operator, value) => {
  return (queryBuilder) => {
    switch (operator) {
      case '==':
      case '===':
        return queryBuilder.eq(field, value);
      case '!=':
      case '!==':
        return queryBuilder.neq(field, value);
      case '<':
        return queryBuilder.lt(field, value);
      case '<=':
        return queryBuilder.lte(field, value);
      case '>':
        return queryBuilder.gt(field, value);
      case '>=':
        return queryBuilder.gte(field, value);
      case 'in':
        return queryBuilder.in(field, value);
      case 'array-contains':
        return queryBuilder.contains(field, [value]);
      default:
        throw new Error(`Unsupported operator: ${operator}`);
    }
  };
};

// Firestore-like orderBy constraint
export const orderBy = (field, direction = 'asc') => {
  return (queryBuilder) => {
    return queryBuilder.order(field, { ascending: direction === 'asc' });
  };
};

// Firestore-like limit constraint
export const limit = (n) => {
  return (queryBuilder) => {
    return queryBuilder.limit(n);
  };
};

// Firestore-like onSnapshot (real-time subscription)
export const onSnapshot = (ref, callback) => {
  const isCollection = ref._collectionName && !ref._docId;
  const isDoc = ref._collectionName && ref._docId;
  const isQuery = ref._query;
  
  let channel;
  
  if (isCollection) {
    // Subscribe to collection changes
    channel = ref._supabase
      .channel(`collection-${ref._collectionName}`)
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: ref._collectionName },
        async () => {
          const snapshot = await getDocs(ref);
          callback(snapshot);
        }
      )
      .subscribe();
  } else if (isDoc) {
    // Subscribe to document changes
    channel = ref._supabase
      .channel(`doc-${ref._collectionName}-${ref._docId}`)
      .on('postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: ref._collectionName,
          filter: `id=eq.${ref._docId}` },
        async () => {
          const snapshot = await getDoc(ref);
          callback(snapshot);
        }
      )
      .subscribe();
  } else if (isQuery) {
    // Subscribe to query results
    const tableName = ref._query._table;
    channel = ref._supabase
      .channel(`query-${tableName}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: tableName },
        async () => {
          const { data, error } = await ref._query;
          if (!error) {
            const snapshot = {
              docs: data.map(item => ({
                id: item.id,
                data: () => item,
                exists: () => true
              })),
              empty: data.length === 0,
              size: data.length };
            callback(snapshot);
          }
        }
      )
      .subscribe();
  }
  
  // Return unsubscribe function
  return () => {
    if (channel) {
      ref._supabase.removeChannel(channel);
    }
  };
};

export const FUNCTION_NAME = {
  SEND_QUOTE: 'send-quote',
  ACCEPT_QUOTE: 'accept-quote',
  UPLOAD_CONTRACT: 'upload-contract',
}

export default supabase
